import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import Header from "../../components/Header";
import useLoader from "../../hooks/useLoader";
import useModal from "../../hooks/useModal";
import useAuthUser from "../../hooks/useAuthUser";
import GrievanceFilter from "../../components/grievance/GrievanceFilter";
import GrievanceList from "../../components/grievance/GrievanceList";
import GrievanceDashboard from "../../components/grievance/GrievanceDashboard";
import Pagination from "../../components/common/Pagination";
import { getMyGrievances, getGrievanceCategories, getGrievanceSubcategories } from "../../services/grievance";
import AddGrievance from "./AddGrievance";
import Button from "../../components/ui/Button";

const GrievancePage = () => {
  const { hideLoader, showLoader } = useLoader();
  const { showModal } = useModal();
  // Only Employees raise grievances; everyone else just views and handles them.
  const { isEmployeeRole, canManageGrievance } = useAuthUser();

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});
  // Set when a dashboard count is clicked, so the filter bar shows (and can
  // clear) the filter the list is opened with.
  const [filterSeed, setFilterSeed] = useState(null);
  // HR Manager / Admin get two tabs — Dashboard first, then the Grievance
  // list. Everyone else only ever sees the list. Mirrored in the URL so the
  // back button from a grievance returns to the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "grievance" ? "grievance" : "dashboard";
  const [categories, setCategories] = useState([]);
  // const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});

  useEffect(() => {
    getGrievanceCategories()
      .then(async (res) => {
        const list = res.data?.data || [];
        setCategories(list);

        // Prefetch every category's subcategories up front, once, so
        // Add never needs to call this API itself — it just reads from state.
        // const entries = await Promise.all(
        //   list.map(async (cat) => {
        //     try {
        //       const subRes = await getGrievanceSubcategories(cat.id);
        //       return [cat.id, subRes.data?.data || []];
        //     } catch (error) {
        //       console.log(`Error loading subcategories for category ${cat.id}:`, error);
        //       return [cat.id, []];
        //     }
        //   })
        // );
        // setSubcategoriesByCategory(Object.fromEntries(entries));
      })
      .catch((error) => console.log("Error loading categories:", error));
    // eslint-disable-next-line
  }, []);

  // Shared by the filter bar and the Add form.
  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) return [];
    // if (subcategoriesByCategory[categoryId]) {
    //   return subcategoriesByCategory[categoryId];
    // }
    const res = await getGrievanceSubcategories(categoryId);
    const list = res.data?.data || [];
    // setSubcategoriesByCategory((prev) => ({ ...prev, [categoryId]: list }));
    return list;
  };

  const fetchGrievances = async (pageNo = 1, params = {}) => {
    try {
      showLoader();
      const res = await getMyGrievances(pageNo, 20, params);
      if (res.status === 200) {
        setData(res.data?.data?.grievances || []);
        setPagination(res.data?.data?.pagination || {});
      } else {
        setData([]);
        setPagination({});
      }
    } catch (error) {
      console.log("Error fetching grievances:", error);
      setData([]);
      setPagination({});
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchGrievances();
    // eslint-disable-next-line
  }, []);

  const filterGrievances = (params = {}) => {
    setActiveFilters(params);
    setPage(1);
    fetchGrievances(1, params);
  };

  const handleTabChange = (newTab) => {
    setSearchParams(newTab === "grievance" ? { tab: "grievance" } : {});
  };

  // A dashboard count was clicked: open the list with that filter applied.
  const openListFromDashboard = (params) => {
    setFilterSeed({ params, nonce: Date.now() });
    filterGrievances(params);
    setSearchParams({ tab: "grievance" });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchGrievances(newPage, activeFilters);
  };

  const handleExport = async (exportParams = {}) => {
    try {
      showLoader();
      const res = await getMyGrievances(1, 10000, exportParams);
      const rows = res.data?.data?.grievances || [];

      const exportData = rows.map((g) => ({
        Reference: g.grievance_ref,
        Department: g.department,
        Category: g.category_name,
        "Sub Category": g.subcategory_name,
        Subject: g.subject,
        Priority: g.priority,
        Confidentiality: g.confidentiality_level,
        Status: g.status_label,
        "Raised On": g.created_at,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Grievances");
      XLSX.writeFile(
        workbook,
        `Grievances Exported - ${new Date().toLocaleDateString()}.xlsx`
      );
    } catch (error) {
      console.log("Error while exporting grievances", error);
    } finally {
      hideLoader();
    }
  };

  const handleAdd = () => {
    showModal(
      "Add Grievance",
      <AddGrievance
        refresh={() => fetchGrievances(page, activeFilters)}
        categories={categories}
        getSubcategories={fetchSubcategories}
      />,
      "lg"
    );
  };

  const list = (
    <>
      <GrievanceFilter
        onFilter={filterGrievances}
        exportGrievances={handleExport}
        categories={categories}
        seed={filterSeed}
      />

      <GrievanceList
        grievanceData={data}
        onFilter={filterGrievances}
        activeFilters={activeFilters}
      />

      {pagination && data.length > 0 && (
        <Pagination
          totalPages={pagination.totalPages}
          currentPage={Number(pagination.pageNo)}
          perPage={Number(pagination.perPage)}
          totalRecords={pagination.totalRecords}
          onPageChange={handlePageChange}
        />
      )}
    </>
  );

  return (
    <div>
      <Header title="Grievance Management">
        {isEmployeeRole && (
          <span className="add float-end">
            <Button onClick={handleAdd}>
              <span className="text">Add Grievance</span>
            </Button>
          </span>
        )}
      </Header>

      {canManageGrievance ? (
        <>
          <ul className="nav nav-tabs" role="tablist" style={{ marginBottom: "12px" }}>
            <li className="nav-item" role="presentation">
              <button
                type="button"
                className={`nav-link ${tab === "dashboard" ? "active" : ""}`}
                onClick={() => handleTabChange("dashboard")}
              >
                Dashboard
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                type="button"
                className={`nav-link ${tab === "grievance" ? "active" : ""}`}
                onClick={() => handleTabChange("grievance")}
              >
                Grievance
              </button>
            </li>
          </ul>
          {tab === "dashboard" ? (
            <GrievanceDashboard onOpenList={openListFromDashboard} />
          ) : (
            list
          )}
        </>
      ) : (
        list
      )}
    </div>
  );
};

export default GrievancePage;
